import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Code2, Copy, Download, ChevronDown, ChevronUp, Cpu, Wifi } from "lucide-react";
import { Button } from "./ui/button";

/**
 * ESP32 Arduino code generator (Cue Genic style).
 * Emits the exact minimal sketch used on the shop floor — same broker,
 * same topic tree (`<root>/<cafe_id>/table/<light_id>/cmd`) as the backend
 * publishes to. Only WiFi + cafe/board specifics are personalized.
 *
 * Props:
 *   cafe    — active cafe { id, name }
 *   devices — provisioned devices [{ id, name, relay_count }]
 *   enabled — cafe.automation_enabled boolean
 */
export default function ESP32CodeGenerator({ cafe, devices, enabled }) {
  const [open, setOpen] = useState(true);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [channelOverride, setChannelOverride] = useState(null); // null → use device's relay_count
  const [mqttCfg, setMqttCfg] = useState({
    host: "",
    port: 8883,
    tls: true,
    topic_root: "cuegenic",
    username: "",
    password: "",
  });
  const [revealed, setRevealed] = useState(false);   // true once step-up succeeds
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealPwd, setRevealPwd] = useState("");
  const [revealErr, setRevealErr] = useState("");
  const [wifiSSID, setWifiSSID] = useState("OnePlus 11R 5G");
  const [wifiPass, setWifiPass] = useState("22222222");

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      try {
        const { data } = await api.get("/platform/mqtt-config");
        setMqttCfg(data);
      } catch {
        /* fallback to defaults */
      }
    })();
  }, [enabled]);

  // Reveal the MQTT password (step-up auth). The masked value is replaced
  // with the plaintext one only after the platform user re-enters their
  // OWN password — this stops a hijacked session from silently leaking the
  // broker secret.
  const revealMqttPassword = async () => {
    setRevealErr("");
    if (!revealPwd) {
      setRevealErr("Enter your Platform password to reveal.");
      return;
    }
    setRevealBusy(true);
    try {
      const { data } = await api.post("/platform/mqtt-config/reveal", {
        password: revealPwd,
      });
      setMqttCfg(data);
      setRevealed(true);
      setRevealPwd("");
      toast.success("MQTT credentials revealed for this session");
    } catch (e) {
      setRevealErr(
        e?.response?.data?.detail || "Couldn't reveal — check your password."
      );
    } finally {
      setRevealBusy(false);
    }
  };

  useEffect(() => {
    if (devices?.length && !selectedDeviceId) {
      setSelectedDeviceId(devices[0].id);
    }
  }, [devices, selectedDeviceId]);

  if (!enabled) return null;

  const selectedDevice = devices?.find((d) => d.id === selectedDeviceId);
  const effectiveChannels = channelOverride ?? (selectedDevice?.relay_count || 4);

  const generateCode = () => {
    const relayCount = effectiveChannels;
    // Safe ESP32 GPIO pins (avoiding strap / flash pins). Same order as the
    // reference sketch: table 1 → GPIO 23, table 2 → 22, ...
    const pinMap4 = [23, 22, 21, 19];
    const pinMap8 = [23, 22, 21, 19, 18, 5, 17, 16];
    const pinMap16 = [23, 22, 21, 19, 18, 5, 17, 16, 4, 2, 15, 13, 12, 14, 27, 26];
    const pins = relayCount === 8 ? pinMap8 : relayCount === 16 ? pinMap16 : pinMap4;
    const tableEntries = pins
      .slice(0, relayCount)
      .map((pin, idx) => `  {"${idx + 1}", ${pin}},`)
      .join("\n");

    const host = mqttCfg.host || "0e30e3d4dfb0469cabdb906761bcce17.s1.eu.hivemq.cloud";
    const port = Number(mqttCfg.port) || 8883;
    const user = mqttCfg.username || "cuegenic";
    const pass = mqttCfg.password || "Ashra$786";
    const root = mqttCfg.topic_root || "cuegenic";
    const cafeId = cafe?.id || "<CAFE_ID>";

    return `#include <WiFi.h>
#include <WiFiClientSecure.h>
#include <PubSubClient.h>
#include <time.h>

// =============== CONFIG ===============
const char* WIFI_SSID     = "${wifiSSID || "YOUR_WIFI_SSID"}";
const char* WIFI_PASSWORD = "${wifiPass || "YOUR_WIFI_PASSWORD"}";

// HiveMQ Cloud
const char* MQTT_HOST     = "${host}";
const int   MQTT_PORT     = ${port};
const char* MQTT_USER     = "${user}";
const char* MQTT_PASS     = "${pass}";

const char* ROOT          = "${root}";
// This must match your cafe's UUID in the app's database.
const char* CAFE_ID       = "${cafeId}";
// ======================================

struct TableMap { const char* light_id; uint8_t pin; };
TableMap TABLES[] = {
${tableEntries}
};
const size_t NUM_TABLES = sizeof(TABLES) / sizeof(TABLES[0]);

WiFiClientSecure net;
PubSubClient mqtt(net);
char topicBuf[160];
unsigned long lastReconnect = 0;

void setRelay(size_t i, bool on) {
  digitalWrite(TABLES[i].pin, on ? LOW : HIGH);   // active-LOW relay module
  snprintf(topicBuf, sizeof(topicBuf), "%s/%s/table/%s/state",
           ROOT, CAFE_ID, TABLES[i].light_id);
  mqtt.publish(topicBuf, on ? "on" : "off", true);
  Serial.printf("Table %s -> %s\\n", TABLES[i].light_id, on ? "ON" : "OFF");
}

void onMessage(char* topic, byte* payload, unsigned int len) {
  String t = topic;
  String msg;
  for (unsigned int i = 0; i < len; i++) msg += (char)payload[i];
  Serial.printf("RX %s = %s\\n", topic, msg.c_str());

  for (size_t i = 0; i < NUM_TABLES; i++) {
    snprintf(topicBuf, sizeof(topicBuf), "%s/%s/table/%s/cmd",
             ROOT, CAFE_ID, TABLES[i].light_id);
    if (t == topicBuf) {
      setRelay(i, msg == "on");
      return;
    }
  }
  Serial.println("(no matching table)");
}

bool connectWifi() {
  Serial.printf("WiFi: connecting to \\"%s\\" ...", WIFI_SSID);
  WiFi.mode(WIFI_STA);
  WiFi.begin(WIFI_SSID, WIFI_PASSWORD);
  for (int i = 0; i < 60 && WiFi.status() != WL_CONNECTED; i++) {
    delay(500);
    Serial.print(".");
  }
  Serial.println();
  if (WiFi.status() != WL_CONNECTED) {
    Serial.println("WiFi: FAILED");
    return false;
  }
  Serial.print("WiFi OK, IP=");
  Serial.println(WiFi.localIP());
  return true;
}

// HiveMQ Cloud uses Let's Encrypt certs — TLS handshake needs the ESP32's
// clock set to a real time, otherwise it fails cert validity checks even
// with setInsecure(). NTP once at boot is enough.
void syncTime() {
  configTime(0, 0, "pool.ntp.org", "time.google.com");
  Serial.print("Sync NTP");
  time_t now = time(nullptr);
  for (int i = 0; i < 20 && now < 8 * 3600; i++) {
    delay(500);
    Serial.print(".");
    now = time(nullptr);
  }
  Serial.printf(" OK  epoch=%lu\\n", (unsigned long)now);
}

bool connectMqtt() {
  if (WiFi.status() != WL_CONNECTED) return false;
  String clientId = String("esp32-") + ROOT + "-" + WiFi.macAddress();
  Serial.printf("MQTT: connecting as %s ... ", clientId.c_str());
  if (!mqtt.connect(clientId.c_str(), MQTT_USER, MQTT_PASS)) {
    Serial.printf("FAILED (state=%d)\\n", mqtt.state());
    return false;
  }
  Serial.println("OK");
  for (size_t i = 0; i < NUM_TABLES; i++) {
    snprintf(topicBuf, sizeof(topicBuf), "%s/%s/table/%s/cmd",
             ROOT, CAFE_ID, TABLES[i].light_id);
    mqtt.subscribe(topicBuf, 1);
    Serial.printf("subscribed: %s\\n", topicBuf);
  }
  // Announce online
  snprintf(topicBuf, sizeof(topicBuf), "%s/%s/device/status", ROOT, CAFE_ID);
  mqtt.publish(topicBuf, "online", true);
  return true;
}

void setup() {
  Serial.begin(115200);
  delay(200);
  Serial.println("\\n=== Cue Genic ESP32 light controller ===");
  Serial.printf("Cafe: %s\\n", CAFE_ID);

  for (size_t i = 0; i < NUM_TABLES; i++) {
    pinMode(TABLES[i].pin, OUTPUT);
    digitalWrite(TABLES[i].pin, HIGH);   // OFF on boot (active-low)
  }

  connectWifi();
  syncTime();

  net.setInsecure();                     // skip cert pinning — good enough for LAN use
  mqtt.setServer(MQTT_HOST, MQTT_PORT);
  mqtt.setBufferSize(1024);
  mqtt.setKeepAlive(30);
  mqtt.setSocketTimeout(15);
  mqtt.setCallback(onMessage);
  connectMqtt();
}

void loop() {
  if (WiFi.status() != WL_CONNECTED) {
    if (millis() - lastReconnect > 5000) {
      lastReconnect = millis();
      connectWifi();
    }
    return;
  }
  if (!mqtt.connected()) {
    if (millis() - lastReconnect > 3000) {
      lastReconnect = millis();
      connectMqtt();
    }
    return;
  }
  mqtt.loop();
}
`;
  };

  const code = generateCode();

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Sketch copied to clipboard");
    } catch {
      toast.error("Copy failed — please select & copy manually");
    }
  };

  const downloadCode = () => {
    const safeName = (cafe?.name || "cue-genic").replace(/[^a-z0-9]+/gi, "_");
    const blob = new Blob([code], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}_esp32.ino`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded .ino file");
  };

  return (
    <div
      className="rounded-2xl border border-white/10 bg-black/30 backdrop-blur"
      data-testid="esp32-code-generator"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors rounded-t-2xl"
        data-testid="esp32-generator-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-400/30 flex items-center justify-center">
            <Cpu className="w-4 h-4 text-amber-300" />
          </div>
          <div className="text-left">
            <div className="text-sm font-black uppercase tracking-widest text-white">
              ESP32 Firmware
            </div>
            <div className="text-[11px] text-gray-500">
              HiveMQ Cloud · TLS 8883 · Ready to flash
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {open && (
        <div className="p-4 space-y-4 border-t border-white/10">
          {/* Reveal MQTT password (step-up auth) */}
          {!revealed && (
            <div
              data-testid="esp32-reveal-panel"
              className="rounded-lg bg-amber-500/5 border border-amber-500/30 p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <Cpu className="w-4 h-4 text-amber-300 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-bold uppercase tracking-widest text-amber-200">
                    Reveal MQTT credentials
                  </div>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    The MQTT password is masked by default. Re-enter your Platform
                    password to bake the real credentials into the sketch.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="esp32-reveal-password"
                  type="password"
                  autoComplete="current-password"
                  value={revealPwd}
                  onChange={(e) => setRevealPwd(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && revealMqttPassword()}
                  placeholder="Your Platform password"
                  className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-amber-400/60"
                />
                <Button
                  onClick={revealMqttPassword}
                  disabled={revealBusy}
                  data-testid="esp32-reveal-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-widest"
                >
                  {revealBusy ? "..." : "Reveal"}
                </Button>
              </div>
              {revealErr && (
                <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1" data-testid="esp32-reveal-error">
                  {revealErr}
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold block mb-1">
                Board
              </label>
              <select
                data-testid="esp32-device-select"
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-amber-400/60"
              >
                {(devices || []).map((d) => (
                  <option key={d.id} value={d.id} className="bg-[#0a0f0d]">
                    {d.name} · {d.relay_count} ch
                  </option>
                ))}
                {(!devices || devices.length === 0) && (
                  <option value="" className="bg-[#0a0f0d]">
                    (no devices yet — add one first)
                  </option>
                )}
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold block mb-1">
                Channels
              </label>
              <select
                data-testid="esp32-channel-override"
                value={channelOverride ?? ""}
                onChange={(e) =>
                  setChannelOverride(e.target.value ? Number(e.target.value) : null)
                }
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-amber-400/60"
              >
                <option value="" className="bg-[#0a0f0d]">
                  Use board default ({selectedDevice?.relay_count || 4})
                </option>
                <option value="4" className="bg-[#0a0f0d]">4</option>
                <option value="8" className="bg-[#0a0f0d]">8</option>
                <option value="16" className="bg-[#0a0f0d]">16</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold block mb-1">
                <Wifi className="inline w-3 h-3 mr-1 -mt-0.5" />
                WiFi SSID
              </label>
              <input
                data-testid="esp32-wifi-ssid"
                type="text"
                value={wifiSSID}
                onChange={(e) => setWifiSSID(e.target.value)}
                placeholder="Your WiFi name"
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-amber-400/60"
              />
            </div>

            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold block mb-1">
                WiFi Password
              </label>
              <input
                data-testid="esp32-wifi-pass"
                type="text"
                value={wifiPass}
                onChange={(e) => setWifiPass(e.target.value)}
                placeholder="Your WiFi password"
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-amber-400/60"
              />
            </div>
          </div>

          {/* Code preview */}
          <div className="relative">
            <div className="absolute top-2 right-2 flex gap-2 z-10">
              <Button
                onClick={copyCode}
                data-testid="esp32-copy-btn"
                variant="outline"
                size="sm"
                disabled={!revealed}
                title={revealed ? "" : "Reveal MQTT password first"}
                className="bg-black/60 border-white/15 text-white hover:bg-white/10 h-8 disabled:opacity-40"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Copy
              </Button>
              <Button
                onClick={downloadCode}
                data-testid="esp32-download-btn"
                size="sm"
                disabled={!revealed}
                title={revealed ? "" : "Reveal MQTT password first"}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold h-8 disabled:opacity-40"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                .ino
              </Button>
            </div>
            <pre
              data-testid="esp32-code-preview"
              className="text-[11px] leading-relaxed bg-[#050807] border border-white/10 rounded-md p-4 overflow-auto max-h-96 text-emerald-200 font-mono whitespace-pre"
            >
              {code}
            </pre>
          </div>

          <div className="text-[11px] text-gray-500 leading-relaxed flex items-start gap-2">
            <Code2 className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
            <p>
              In the Arduino IDE install <b>PubSubClient</b> by Nick O&apos;Leary,
              then select board <b>ESP32 Dev Module</b> and upload. The board
              subscribes to <code className="text-amber-300">{mqttCfg.topic_root || "cuegenic"}/&lt;cafe_id&gt;/table/&lt;n&gt;/cmd</code>
              &nbsp;and toggles the matching relay on <code className="text-amber-300">on</code> / <code className="text-amber-300">off</code>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
